import { CommonModule, DatePipe } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ButtonModule } from "primeng/button";
import SyncStatusService, { SyncStatus } from "../../../../../core/sync/sync-status-service";
import SyncManager from "../../../../../core/sync/sync-manager";
import { TagModule } from "primeng/tag";
import { JobStatus } from "../job-status";
import { DividerModule } from "primeng/divider";
import { Router } from "@angular/router";
import { $dt } from "@primeng/themes";
import { BqaJobSyncMetadata, DataSyncMetadataService, SyncMetadata } from "../../../../../core/sync/data-sync/data-sync-metadata.service";

interface JobSyncStatusSheetInfo {
  badgeSeverity: string;
  badgeText: string;
  badgeIconClass: string;
  message1: string;
  message2: string;
  actionButtonText: string;
}

enum JobSyncStatusState {
  CompleteUploaded = JobStatus.CompleteUploaded,
  CompleteInProgress = JobStatus.CompleteInProgress,
  CompletePending = JobStatus.CompletePending,
  CompleteOffline = JobStatus.CompleteOffline,
  Overdue = JobStatus.Overdue
}

interface JobSyncProgress {
  current: number;
  total: number;
}

interface JobSyncProgressTotals {
  images: JobSyncProgress;
  notes: JobSyncProgress;
  other: JobSyncProgress;
}

interface JobSyncProgressDisplay {
  label: string;
  value: JobSyncProgress;
}

interface JobSyncProgressGuage {
  percentComplete: number;
  percentCompleteSvgDashValue: string;
  textXPosition: string;
  fillColor: string;
  strokeColor: string;
  fontColor: string;
  fontWeight: string;
  showCheck: boolean;
}

const syncStatusSheetInfoMap: Record<JobSyncStatusState, JobSyncStatusSheetInfo> = {
  [JobSyncStatusState.CompleteUploaded]: {
    badgeSeverity: "success",
    badgeText: "Uploaded",
    badgeIconClass: "pi pi-check-circle",
    message1: "Your work has been <strong>successfully uploaded.</strong>",
    message2: "",
    actionButtonText: ""
  },
  [JobSyncStatusState.CompleteInProgress]: {
    badgeSeverity: "info",
    badgeText: "Upload in Progress",
    badgeIconClass: "pi pi-sync",
    message1: "Your work is currently uploading.",
    message2: "To ensure your work is saved, please <strong>keep this app open</strong> until the upload is 100% complete.",
    actionButtonText: ""
  },
  [JobSyncStatusState.CompletePending]: {
    badgeSeverity: "info",
    badgeText: "Upload in Progress",
    badgeIconClass: "pi pi-sync",
    message1: "Your upload is incomplete because <strong>{count} items are saved only to this device.</strong>",
    message2: "To ensure your work is saved, please <strong>keep this app open</strong> until the upload completes.",
    actionButtonText: "Retry Pending Items"
  },
  [JobSyncStatusState.CompleteOffline]: {
    badgeSeverity: "warn",
    badgeText: "Upload Pending",
    badgeIconClass: "pi pi-sync",
    message1: "Your work is <strong>saved to this device</strong>, but the upload is paused because you are currently offline.",
    message2: "To upload your work, connect to the internet and <strong>keep this app open.</strong>",
    actionButtonText: "",
  },
  [JobSyncStatusState.Overdue]: {
    badgeSeverity: "danger",
    badgeText: "Overdue",
    badgeIconClass: "pi pi-exclamation-circle",
    message1: "This {jobActionName} is past its scheduled completion date.",
    message2: "Please complete this work as soon as possible.",
    actionButtonText: "Open {jobActionName}",
  }
};

const svgSize = 80;

@Component({
  selector: "job-sync-status-drawer",
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, DividerModule],
  providers: [DatePipe],
  templateUrl: "./job-sync-status-drawer.component.html",
  styleUrl: "./job-sync-status-drawer.component.scss"
})
export class SyncStatusSheetComponent {
  constructor(
    private syncManager: SyncManager,
    private syncStatusService: SyncStatusService,
    private router: Router,
    private dataSyncMetadataService: DataSyncMetadataService
  ) { }

  @Input() jobMetadata: BqaJobSyncMetadata | null = null;
  @Input() jobStatus: JobStatus | null = null;
  @Input() jobId: number | null = null;
  @Input() overdueJobRoute: string | null = null;
  @Input() jobActionName: string;
  @Output() onSyncSheetClose = new EventEmitter<void>();

  svgSize = svgSize;

  groupedMetadata(): JobSyncProgressDisplay[] {
    const groupedData = Object.entries(this.jobMetadata || {})
      .filter(([_key, value]: [string, SyncMetadata | number | string]) => typeof value === "object")
      .reduce<JobSyncProgressTotals>((acc, prev) => {
        const [key, value] = prev as [keyof BqaJobSyncMetadata, SyncMetadata];
        const { localTotalCount, remoteCount } = this.dataSyncMetadataService.getEffectiveSyncMetadata(value);

        // NOTE: if a job was completed on a different device the new device may not have all the original data in the indexedDB
        // This is because for completed jobs we pull them without all the extra data as it's not needed.
        // So for this we don't want to show X / 0 items synced, instead we want to show X / X items synced to indicate that while we may not have the full data locally, everything that we do have is synced.
        if (key === "images") {
          acc.images.current += remoteCount;
          acc.images.total += localTotalCount || remoteCount;
        } else if (key === "issues") {
          acc.notes.current += remoteCount;
          acc.notes.total += localTotalCount || remoteCount;
        } else {
          acc.other.current += remoteCount;
          acc.other.total += localTotalCount || remoteCount;
        }

        return acc;
      }, {
        images: { current: 0, total: 0 },
        notes: { current: 0, total: 0 },
        other: { current: 0, total: 0 }
      } as JobSyncProgressTotals);

    return [
      { label: "Images", value: groupedData.images },
      { label: "Notes", value: groupedData.notes },
      { label: "Other", value: groupedData.other },
    ];
  }

  /**
   * Embedded views refresh after host bindings, so a side-effect assignment reads stale on the first pass
   * and triggers ExpressionChangedAfterItHasBeenCheckedError (NG0100).
   */
  getPendingCount(): number {
    return this.groupedMetadata()
      .reduce((pending, item) => pending + (item.value.total - item.value.current), 0);
  }

  progressGuageValues(): JobSyncProgressGuage {
    const groupedData = this.groupedMetadata();
    const totalItems = groupedData.reduce((sum, item) => sum + item.value.total, 0);
    const totalSynced = groupedData.reduce((sum, item) => sum + item.value.current, 0);
    const rawPercent = totalItems === 0 ? 100 : Math.floor((totalSynced / totalItems) * 100);

    const radius = (svgSize / 2) - 10;
    const circumference = 3.14 * radius * 2;
    const svgDashValue = `${Math.round(circumference * ((100 - rawPercent) / 100))}px`;

    const progressGaugeValues = {
      percentComplete: rawPercent,
      percentCompleteSvgDashValue: svgDashValue.toString(),
      textXPosition: rawPercent === 100 ? "20px" : "25px",
      fillColor: rawPercent === 100 ? "#229326" : "transparent",
      strokeColor: rawPercent === 100 ? "#229326" : "#96de78",
      fontColor: rawPercent === 100 ? "#FFFFFF" : "#000000",
      fontWeight: rawPercent === 100 ? "normal" : "bold",
      showCheck: rawPercent === 100
    };

    if (this.getSyncStatusState() === JobSyncStatusState.CompleteOffline) {
      progressGaugeValues.strokeColor = $dt("gray.100").value;
    }

    if (this.getSyncStatusState() === JobSyncStatusState.CompletePending) {
      progressGaugeValues.strokeColor = $dt("gray.300").value;
    }
    return progressGaugeValues;
  }

  jobSyncStatusStateEnum = JobSyncStatusState;

  getSyncStatusState(): JobSyncStatusState {
    if (this.jobStatus === JobStatus.CompleteInProgress) {
      return JobSyncStatusState.CompleteInProgress;
    } else if (this.jobStatus === JobStatus.CompleteOffline) {
      return JobSyncStatusState.CompleteOffline;
    } else if (this.jobStatus === JobStatus.Overdue) {
      return JobSyncStatusState.Overdue;
    } else if (this.jobStatus === JobStatus.CompletePending) {
      return JobSyncStatusState.CompletePending;
    }
    return JobSyncStatusState.CompleteUploaded;
  }

  getSyncStatusInfo(): JobSyncStatusSheetInfo {
    const statusState = this.getSyncStatusState();
    return syncStatusSheetInfoMap[statusState];
  }

  getMessageWithCount(message: string, pendingCount: number): string {
    return message.replaceAll("{count}", pendingCount.toString());
  }

  getMessageWithJobActionName(message: string, jobActionName: string): string {
    return message.replaceAll("{jobActionName}", jobActionName);
  }

  onSyncStatusAction(): void {
    this.onSyncSheetClose.emit();

    if (this.jobStatus === JobStatus.Overdue) {
      if (this.overdueJobRoute) {
        this.router.navigateByUrl(this.overdueJobRoute);
      } else {
        throw new Error("Overdue job route is not defined.");
      }
    } else {
      this.syncStatusService.setSyncStatus(SyncStatus.InProgress);

      const isForceSync = false;
      const sendJobsIndividually = true;
      this.syncManager.manuallyTriggerSync(isForceSync, sendJobsIndividually);
    }
  }
}